import { useState } from "react";
import { FieldErrors, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { PersonalDetails } from "@/apps/partner/components/PersonalDetails/PersonalDetails";
import { AddDeviceFormType } from "@/apps/partner/types";
import {
  partnerAddDeviceStepPaths,
  partnerAddDeviceSubsteps,
} from "@/apps/partner/utils/partnerAddDevice.constants";
import { transformFormDataToPrepareBasketRequest } from "@/apps/partner/utils/transformPrepareBasketRequest";
import { Button, ButtonLink } from "@/components/Interface/Button/Button";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { useHandleError } from "@/hooks/useHandleError";
import { prepareBasket } from "@/services/api";
import { getHeader } from "@/utils/basketUtils";

/** Submitted fields whose inputs (and error messages) live on an earlier step. */
const earlierStepOfField: Partial<
  Record<keyof AddDeviceFormType, (typeof partnerAddDeviceSubsteps)[number]>
> = {
  simType: "sim-type",
  productId: "rate-selection",
  simAlias: "rate-selection",
  onp: "rate-selection",
  quantity: "quantity",
};

export const PartnerPersonalDetailsStep = () => {
  const { t } = useTranslation("partnerPortal", { keyPrefix: "addDevice" });
  const { t: tCommon } = useTranslation();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { handleSubmit } = useFormContext<AddDeviceFormType>();

  // An error on an earlier step is only rendered there (e.g. a rate that was
  // cleared after going back), so take the partner to that step instead of
  // failing the submit without any visible feedback.
  const onInvalid = (errors: FieldErrors<AddDeviceFormType>) => {
    const invalidFields = Object.keys(errors) as (keyof AddDeviceFormType)[];
    const earliestInvalidStep = partnerAddDeviceSubsteps.find((substep) =>
      invalidFields.some((field) => earlierStepOfField[field] === substep),
    );

    if (earliestInvalidStep) {
      navigate(partnerAddDeviceStepPaths[earliestInvalidStep]);
    }
  };

  const onSubmit = async (data: AddDeviceFormType) => {
    setIsSubmitting(true);

    try {
      const header = getHeader();
      const payload = transformFormDataToPrepareBasketRequest(data, header);
      const quantity = data.quantity ?? 1;

      for (let i = 0; i < quantity; i++) {
        await prepareBasket(payload);
      }

      sessionStorage.setItem("orderSuccess", "true");
      sessionStorage.setItem(
        "orderSuccessCustomerType",
        data.isExistingCustomer ? "existing" : "new",
      );
      navigate("/partner/add-sim/success");
    } catch (error) {
      handleError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex w-full flex-col gap-6">
      <PersonalDetails />

      <StepFlowNavigation
        primaryButton={
          <Button
            dataTestid="submit-onboarding-button"
            variant="primary"
            disabled={isSubmitting}
            onClick={handleSubmit(onSubmit, onInvalid)}
          >
            {isSubmitting ? t("submitting") : t("submit")}
          </Button>
        }
        secondaryButton={
          <ButtonLink
            variant="secondary"
            to={partnerAddDeviceStepPaths.quantity}
          >
            {tCommon("Common.label.back")}
          </ButtonLink>
        }
      />
    </div>
  );
};
